import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Storefront, Plus, PencilSimple, Trash, Image as ImageIcon, Package, Receipt, ArrowSquareOut, CheckCircle, Warning, DownloadSimple, DotsSixVertical, FloppyDisk } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShippingAdmin } from "@/components/ShippingAdmin";
import { useApp } from "@/context/AppContext";
import { ColorSwatch, RAINBOW_BG, CARBON_BG } from "@/components/VariantPreview";
import api, { API } from "@/lib/api";

const emptyForm = { name: "", description: "", price: "0", codes_count: "1", visible: true, min_qty: "1", max_qty: "10", colors: [], motifs: [], ptype: "physical", delivery: "activation_code", delivery_time: "", custom_motif: false };
const money = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export function ShopAdmin() {
  const { t, setShopEnabled } = useApp();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const variantFileRef = useRef(null);
  const variantTarget = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [paypalOk, setPaypalOk] = useState(false);
  const [products, setProducts] = useState([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragIdx = useRef(null);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [exportMonth, setExportMonth] = useState("");

  const load = async () => {
    try {
      const [s, p, o] = await Promise.all([
        api.get("/admin/shop/settings"),
        api.get("/admin/shop/products"),
        api.get("/admin/shop/orders"),
      ]);
      setEnabled(s.data.enabled);
      setPaypalOk(s.data.paypal_configured);
      setProducts(p.data);
      setOrders(o.data);
      setOrderDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Laden des Shops"); }
  };
  useEffect(() => { load(); }, []);

  const onDropRow = (targetIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === targetIdx) return;
    setProducts((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(targetIdx, 0, moved);
      return arr;
    });
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.post("/admin/shop/products/reorder", { ids: products.map((p) => p.id) });
      setOrderDirty(false);
      toast.success("Reihenfolge gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSavingOrder(false); }
  };

  const exportOrders = async () => {
    try {
      const q = exportMonth ? `?month=${exportMonth}` : "";
      const res = await api.get(`/admin/shop/orders/export${q}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `safebuddy-bestellungen${exportMonth ? "-" + exportMonth : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export fehlgeschlagen"); }
  };

  const toggleShop = async (v) => { setEnabled(v); await api.put("/admin/shop/settings", { enabled: v }); setShopEnabled(v); toast.success(t("saved")); };

  const openNew = () => { setEditing(null); setForm(emptyForm); setImageFile(null); setImagePreview(""); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", price: String(p.price ?? 0), codes_count: String(p.codes_count ?? 1),
      visible: !!p.visible, min_qty: String(p.min_qty ?? 1), max_qty: String(p.max_qty ?? 10),
      ptype: p.ptype || "physical", delivery: p.delivery || "activation_code",
      delivery_time: p.delivery_time || "", custom_motif: !!p.custom_motif,
      colors: (p.colors || []).map((c) => ({ ...c, price_delta: String(c.price_delta ?? 0) })),
      motifs: (p.motifs || []).map((m) => ({ ...m, price_delta: String(m.price_delta ?? 0) })),
    });
    setImageFile(null);
    setImagePreview(p.image_path ? `${API}/files/${p.image_path}` : "");
    setDialogOpen(true);
  };

  const onPickImage = (e) => { const f = e.target.files?.[0]; if (!f) return; setImageFile(f); setImagePreview(URL.createObjectURL(f)); };

  // variant helpers
  const addColor = () => setForm((f) => ({ ...f, colors: [...f.colors, { name: "", hex: "#A855F7", image_path: "", description: "", price_delta: "0" }] }));
  const addSpecialColor = (special, name) => setForm((f) => ({ ...f, colors: [...f.colors, { name, special, hex: "", image_path: "", description: "", price_delta: "0" }] }));
  const addMotif = () => setForm((f) => ({ ...f, motifs: [...f.motifs, { name: "", image_path: "", description: "", price_delta: "0" }] }));
  const updVariant = (kind, i, field, val) => setForm((f) => { const arr = [...f[kind]]; arr[i] = { ...arr[i], [field]: val }; return { ...f, [kind]: arr }; });
  const delVariant = (kind, i) => setForm((f) => ({ ...f, [kind]: f[kind].filter((_, idx) => idx !== i) }));
  const pickVariantImage = (kind, i) => { variantTarget.current = { kind, i }; variantFileRef.current?.click(); };
  const onVariantImage = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !variantTarget.current) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/shop/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { kind, i } = variantTarget.current;
      updVariant(kind, i, "image_path", r.data.image_path);
    } catch (err) { toast.error("Upload fehlgeschlagen"); }
  };

  const saveProduct = async () => {
    if (!form.name.trim()) { toast.error(t("productName")); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(), description: form.description.trim(),
        price: parseFloat(form.price) || 0, codes_count: Math.max(1, parseInt(form.codes_count) || 1),
        visible: !!form.visible, min_qty: Math.max(1, parseInt(form.min_qty) || 1), max_qty: Math.max(1, parseInt(form.max_qty) || 1),
        ptype: form.ptype, delivery: form.delivery,
        delivery_time: (form.delivery_time || "").trim(), custom_motif: !!form.custom_motif,
        colors: form.colors.map((c) => ({ id: c.id, name: (c.name || "").trim(), hex: c.hex || "", image_path: c.image_path || "", special: c.special || "", description: (c.description || "").trim(), price_delta: parseFloat(c.price_delta) || 0 })).filter((c) => c.name),
        motifs: form.motifs.map((m) => ({ id: m.id, name: (m.name || "").trim(), image_path: m.image_path || "", description: (m.description || "").trim(), price_delta: parseFloat(m.price_delta) || 0 })).filter((m) => m.name || m.image_path),
      };
      let pid;
      if (editing) { await api.put(`/admin/shop/products/${editing.id}`, payload); pid = editing.id; }
      else { const r = await api.post("/admin/shop/products", payload); pid = r.data.id; }
      if (imageFile && pid) {
        const fd = new FormData(); fd.append("file", imageFile);
        await api.post(`/admin/shop/products/${pid}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("saved")); setDialogOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(t("deleteProductConfirm"))) return;
    await api.delete(`/admin/shop/products/${p.id}`); toast.success(t("saved")); await load();
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const statusMap = { pending: { label: t("orderPending"), color: "#F59E0B" }, paid: { label: t("orderPaid"), color: "#22C55E" }, failed: { label: t("orderFailed"), color: "#EF4444" } };

  return (
    <div className="sb-card p-5" data-testid="shop-admin">
      <h2 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2"><Storefront size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />{t("shopAdminTitle")}</h2>

      <div className="flex items-center justify-between border-2 border-gray-800 rounded-2xl px-3 py-2.5 bg-white">
        <div className="min-w-0 pr-2">
          <span className="font-body text-sm block">{t("shopEnabledLabel")}</span>
          <span className="font-body text-[11px] text-gray-500 block leading-tight">{t("shopEnabledHint")}</span>
        </div>
        <Switch data-testid="shop-enabled-toggle" checked={enabled} onCheckedChange={toggleShop} />
      </div>

      <div className={`mt-3 flex items-start gap-2 rounded-2xl border-2 px-3 py-2 ${paypalOk ? "border-green-600" : "border-amber-500"}`} style={{ background: paypalOk ? "#F0FDF4" : "#FFFBEB" }} data-testid="paypal-status">
        {paypalOk ? <CheckCircle size={20} weight="fill" className="shrink-0 mt-0.5 text-green-600" /> : <Warning size={20} weight="fill" className="shrink-0 mt-0.5 text-amber-500" />}
        <span className="font-body text-sm text-gray-800">{paypalOk ? t("paypalConfigured") : t("paypalNotConfigured")}</span>
      </div>

      <div className="mt-3 flex gap-2">
        <button data-testid="shop-preview-btn" onClick={() => navigate("/shop")} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1"><ArrowSquareOut size={16} weight="bold" />{t("shopPreview")}</button>
      </div>

      {/* Products */}
      <div className="mt-5 flex items-center justify-between">
        <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><Package size={18} weight="bold" />{t("shopProductsTitle")}</h3>
        <div className="flex items-center gap-2">
          {orderDirty && (
            <button data-testid="save-order-btn" onClick={saveOrder} disabled={savingOrder} className="sb-btn px-3 py-2 text-white text-sm font-medium flex items-center gap-1 disabled:opacity-50" style={{ background: "#22C55E" }}><FloppyDisk size={16} weight="bold" />{savingOrder ? "…" : "Reihenfolge speichern"}</button>
          )}
          <button data-testid="new-product-btn" onClick={openNew} className="sb-btn px-3 py-2 text-white text-sm font-medium flex items-center gap-1" style={{ background: "var(--app-primary)" }}><Plus size={16} weight="bold" />{t("newProduct")}</button>
        </div>
      </div>
      <p className="font-body text-[11px] text-gray-400 mt-1">Ziehe Produkte am Griff, um die Reihenfolge im Shop festzulegen.</p>
      <div className="space-y-2 mt-3" data-testid="admin-products-list">
        {products.length === 0 && <p className="font-body text-sm text-gray-500">{t("noProducts")}</p>}
        {products.map((p, idx) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`}
            draggable
            onDragStart={() => { dragIdx.current = idx; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onDropRow(idx); }}
            className="border-2 border-gray-800 rounded-2xl p-3 bg-white flex gap-3">
            <div data-testid={`drag-handle-${p.id}`} className="flex items-center text-gray-400 cursor-grab active:cursor-grabbing shrink-0 -ml-1" title="Ziehen zum Sortieren"><DotsSixVertical size={22} weight="bold" /></div>
            <div className="h-16 w-16 rounded-xl border-2 border-gray-800 overflow-hidden shrink-0 flex items-center justify-center bg-gray-50">
              {p.image_path ? <img src={`${API}/files/${p.image_path}`} alt={p.name} className="h-full w-full object-cover" /> : <ImageIcon size={24} className="text-gray-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-heading font-semibold text-gray-900 truncate">{p.name}</span>
                <span className="text-[10px] font-heading font-semibold px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: p.visible ? "#22C55E" : "#9CA3AF" }}>{p.visible ? t("visibleBadge") : t("hidden")}</span>
              </div>
              <p className="font-body text-xs text-gray-500 line-clamp-2">{p.description}</p>
              <p className="font-body text-sm mt-1"><b>{money(p.price)}</b> · {p.codes_count} {t("codesIncluded")}{(p.colors?.length || p.motifs?.length) ? ` · ${(p.colors?.length || 0)}🎨 ${(p.motifs?.length || 0)}🖼️` : ""}</p>
              <div className="flex gap-2 mt-2">
                <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><PencilSimple size={14} weight="bold" />{t("edit")}</button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => removeProduct(p)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1 text-red-600"><Trash size={14} weight="bold" />{t("delete")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Shipping */}
      <ShippingAdmin />

      {/* Orders */}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><Receipt size={18} weight="bold" />{t("shopOrdersTitle")}</h3>
        <span className="font-body text-xs text-gray-400">({orders.length})</span>
        {orders.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <input data-testid="export-month-input" type="month" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)} className="sb-input !py-1.5 text-xs w-36" title="Monatsfilter (optional)" />
            {exportMonth && <button data-testid="export-month-clear" onClick={() => setExportMonth("")} className="text-xs text-gray-400 underline">alle</button>}
            <button data-testid="export-orders-btn" onClick={exportOrders} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><DownloadSimple size={14} weight="bold" />CSV-Export</button>
          </div>
        )}
      </div>
      <div className="space-y-2 mt-3" data-testid="admin-orders-list">
        {orders.length === 0 && <p className="font-body text-sm text-gray-500">{t("noOrders")}</p>}
        {orders.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.id}`} className="border-2 border-gray-800 rounded-2xl p-3 bg-white">
            <div className="flex items-center justify-between gap-2">
              <span className="font-heading font-semibold text-gray-900 truncate">{o.product_name}</span>
              <span className="text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: (statusMap[o.status] || {}).color || "#6B7280" }}>{(statusMap[o.status] || {}).label || o.status}</span>
            </div>
            <p className="font-body text-xs text-gray-500 break-all">{t("buyer")}: {o.customer_name || ""} · {o.customer_email || o.buyer_email || "—"}</p>
            {o.address && <p className="font-body text-xs text-gray-500 break-words">{o.address}</p>}
            <p className="font-body text-xs text-gray-500">{money(o.amount)} ({t("shippingCostLabel")}: {money(o.shipping_cost || 0)}{o.shipping?.name ? ` · ${o.shipping.name}` : ""}) · {fmt(o.created_at)}</p>
            {(o.activation_codes || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {o.activation_codes.map((c) => (<span key={c} className="text-[11px] font-heading font-semibold bg-gray-100 border border-gray-300 rounded-md px-1.5 py-0.5">{c}</span>))}
              </div>
            )}
            {(o.items || []).filter((li) => li.motif_image_path).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`order-motifs-${o.id}`}>
                {(o.items || []).filter((li) => li.motif_image_path).map((li, idx) => (
                  <a key={idx} data-testid={`order-motif-${o.id}-${idx}`} href={`${API}/files/${li.motif_image_path}`} target="_blank" rel="noreferrer" download
                    className="text-[11px] font-heading font-semibold text-white rounded-md px-2 py-1 flex items-center gap-1" style={{ background: "#2563EB" }}>
                    <DownloadSimple size={12} weight="bold" />Wunschmotiv{(o.items || []).filter((l) => l.motif_image_path).length > 1 ? ` ${idx + 1}` : ""}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* hidden inputs */}
      <input ref={variantFileRef} type="file" accept="image/*" onChange={onVariantImage} className="hidden" data-testid="variant-image-input" />

      {/* Product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? t("editProductTitle") : t("newProduct")}</DialogTitle>
            <DialogDescription>{t("shopAdminTitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2" data-testid="product-type-selector">
              <button type="button" data-testid="ptype-physical" onClick={() => setForm({ ...form, ptype: "physical" })} className={`py-2.5 rounded-xl border-2 font-heading font-medium text-sm ${form.ptype === "physical" ? "text-white" : "bg-white text-gray-700"}`} style={form.ptype === "physical" ? { background: "var(--app-primary)", borderColor: "var(--app-primary)" } : { borderColor: "#1F2937" }}>📦 Physisches Produkt</button>
              <button type="button" data-testid="ptype-digital" onClick={() => setForm({ ...form, ptype: "digital" })} className={`py-2.5 rounded-xl border-2 font-heading font-medium text-sm ${form.ptype === "digital" ? "text-white" : "bg-white text-gray-700"}`} style={form.ptype === "digital" ? { background: "var(--app-primary)", borderColor: "var(--app-primary)" } : { borderColor: "#1F2937" }}>💾 Digitales Produkt</button>
            </div>
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("productName")}</label>
              <input data-testid="product-name-input" className="sb-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("productDesc")}</label>
              <textarea data-testid="product-desc-input" rows={3} className="sb-input mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className={form.ptype === "digital" ? "" : "grid grid-cols-2 gap-3"}>
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("productPrice")}</label>
                <input data-testid="product-price-input" type="number" min="0" step="0.01" className="sb-input mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              {form.ptype === "physical" && (
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("productCodesCount")}</label>
                <input data-testid="product-codes-input" type="number" min="1" className="sb-input mt-1" value={form.codes_count} onChange={(e) => setForm({ ...form, codes_count: e.target.value })} />
              </div>
              )}
            </div>
            {form.ptype === "physical" && (
              <div className="space-y-3" data-testid="physical-extra-fields">
                <div>
                  <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Lieferzeit (z. B. „5–7 Werktage")</label>
                  <input data-testid="product-delivery-time" className="sb-input mt-1" value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} placeholder="5–7 Werktage" />
                </div>
                <div className="flex items-center justify-between border-2 border-gray-200 rounded-xl px-3 py-2.5">
                  <div>
                    <span className="font-body text-sm font-medium text-gray-900">Kunden-Wunschmotiv (Bild-Upload)</span>
                    <p className="font-body text-[11px] text-gray-500">Kunde lädt beim Kauf ein eigenes Bild für UV-Druck hoch.</p>
                  </div>
                  <Switch data-testid="product-custom-motif" checked={form.custom_motif} onCheckedChange={(v) => setForm({ ...form, custom_motif: v })} />
                </div>
              </div>
            )}
            {form.ptype === "digital" && (
              <div data-testid="digital-delivery-wrap">
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Digitale Auslieferung</label>
                <select data-testid="product-delivery-select" className="sb-input mt-1" value={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.value })}>
                  <option value="activation_code">Aktivierungscode (automatisch)</option>
                  <option value="file_download">Datei-Download</option>
                  <option value="manual">Manuelle Auslieferung</option>
                </select>
              </div>
            )}
            {form.ptype === "physical" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("minQtyLabel")}</label>
                <input data-testid="product-minqty-input" type="number" min="1" className="sb-input mt-1" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("maxQtyLabel")}</label>
                <input data-testid="product-maxqty-input" type="number" min="1" className="sb-input mt-1" value={form.max_qty} onChange={(e) => setForm({ ...form, max_qty: e.target.value })} />
              </div>
            </div>
            )}
            <div className="flex items-center justify-between border-2 border-gray-800 rounded-2xl px-3 py-2.5 bg-white">
              <span className="font-body text-sm">{t("productVisible")}</span>
              <Switch data-testid="product-visible-toggle" checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} />
            </div>

            {/* main image */}
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("productImage")}</label>
              <div className="mt-1 flex items-center gap-3">
                <div className="h-16 w-16 rounded-xl border-2 border-gray-800 overflow-hidden shrink-0 flex items-center justify-center bg-gray-50">
                  {imagePreview ? <img src={imagePreview} alt="preview" className="h-full w-full object-cover" /> : <ImageIcon size={24} className="text-gray-300" />}
                </div>
                <input ref={fileRef} data-testid="product-image-input" type="file" accept="image/*" onChange={onPickImage} className="hidden" />
                <button type="button" onClick={() => fileRef.current?.click()} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1"><ImageIcon size={16} weight="bold" />{t("uploadImage")}</button>
              </div>
            </div>

            {/* colors */}
            <div className="border-t-2 border-gray-200 pt-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("colorsLabel")}</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid="add-rainbow-btn" type="button" onClick={() => addSpecialColor("rainbow", "Regenbogen")} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><span className="h-3.5 w-3.5 rounded-full" style={{ background: RAINBOW_BG }} />Regenbogen</button>
                  <button data-testid="add-carbon-btn" type="button" onClick={() => addSpecialColor("carbon", "Carbon Look")} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><span className="h-3.5 w-3.5 rounded-full" style={CARBON_BG} />Carbon Look</button>
                  <button data-testid="add-color-btn" type="button" onClick={addColor} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><Plus size={14} weight="bold" />{t("addColor")}</button>
                </div>
              </div>
              <div className="space-y-2 mt-2" data-testid="colors-editor">
                {form.colors.map((c, i) => (
                  <div key={i} className="border-2 border-gray-300 rounded-xl p-2 space-y-2" data-testid={`color-edit-${i}`}>
                    <div className="flex items-center gap-2">
                      {c.special
                        ? <div className="flex items-center gap-2 flex-1"><ColorSwatch color={c} size={28} /><input data-testid={`color-name-${i}`} placeholder={t("variantName")} className="sb-input !py-1.5 text-sm flex-1" value={c.name} onChange={(e) => updVariant("colors", i, "name", e.target.value)} /></div>
                        : <><input data-testid={`color-name-${i}`} placeholder={t("variantName")} className="sb-input !py-1.5 text-sm flex-1" value={c.name} onChange={(e) => updVariant("colors", i, "name", e.target.value)} /><input data-testid={`color-hex-${i}`} type="color" className="h-9 w-10 rounded border-2 border-gray-800" value={c.hex || "#000000"} onChange={(e) => updVariant("colors", i, "hex", e.target.value)} /></>}
                      <button type="button" onClick={() => delVariant("colors", i)} className="text-red-500 p-1"><Trash size={16} weight="bold" /></button>
                    </div>
                    <input data-testid={`color-desc-${i}`} placeholder={t("variantDesc")} className="sb-input !py-1.5 text-sm" value={c.description} onChange={(e) => updVariant("colors", i, "description", e.target.value)} />
                    <div className="flex items-center gap-2">
                      <input data-testid={`color-surcharge-${i}`} type="number" step="0.01" placeholder={t("variantSurcharge")} className="sb-input !py-1.5 text-sm w-28" value={c.price_delta} onChange={(e) => updVariant("colors", i, "price_delta", e.target.value)} />
                      {!c.special && <button type="button" onClick={() => pickVariantImage("colors", i)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><ImageIcon size={14} weight="bold" />{t("variantImage")}</button>}
                      {!c.special && c.image_path && <img src={`${API}/files/${c.image_path}`} alt="" className="h-8 w-8 rounded object-cover border border-gray-400" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* motifs */}
            <div className="border-t-2 border-gray-200 pt-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("motifsLabel")}</label>
                <button data-testid="add-motif-btn" type="button" onClick={addMotif} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><Plus size={14} weight="bold" />{t("addMotif")}</button>
              </div>
              <div className="space-y-2 mt-2" data-testid="motifs-editor">
                {form.motifs.map((m, i) => (
                  <div key={i} className="border-2 border-gray-300 rounded-xl p-2 space-y-2" data-testid={`motif-edit-${i}`}>
                    <div className="flex items-center gap-2">
                      <input data-testid={`motif-name-${i}`} placeholder={t("variantName")} className="sb-input !py-1.5 text-sm flex-1" value={m.name} onChange={(e) => updVariant("motifs", i, "name", e.target.value)} />
                      <button type="button" onClick={() => delVariant("motifs", i)} className="text-red-500 p-1"><Trash size={16} weight="bold" /></button>
                    </div>
                    <input data-testid={`motif-desc-${i}`} placeholder={t("variantDesc")} className="sb-input !py-1.5 text-sm" value={m.description} onChange={(e) => updVariant("motifs", i, "description", e.target.value)} />
                    <div className="flex items-center gap-2">
                      <input data-testid={`motif-surcharge-${i}`} type="number" step="0.01" placeholder={t("variantSurcharge")} className="sb-input !py-1.5 text-sm w-28" value={m.price_delta} onChange={(e) => updVariant("motifs", i, "price_delta", e.target.value)} />
                      <button type="button" onClick={() => pickVariantImage("motifs", i)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><ImageIcon size={14} weight="bold" />{t("variantImage")}</button>
                      {m.image_path && <img src={`${API}/files/${m.image_path}`} alt="" className="h-8 w-8 rounded object-cover border border-gray-400" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button data-testid="save-product-btn" onClick={saveProduct} disabled={busy} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{t("saveProduct")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
